import { useState, type ReactNode } from 'react';
import { useRecordContext, type RaRecord } from 'react-admin';
import {
  Box,
  ButtonBase,
  Card,
  CardContent,
  Chip,
  Collapse,
  Stack,
  Table,
  TableBody,
  Typography,
} from '@mui/material';
import ExpandMore from '@mui/icons-material/ExpandMore';
import ExpandLess from '@mui/icons-material/ExpandLess';
import { Row } from '../../../shared/Panel';
import { plNumber } from '../../../shared/number';
import { ClientFromPreOrder, windowText } from '../bankRowText';
import { SourceCell } from '../cells';
import { GeoPointsInfo } from '../geoPoint';
import { bankRowWeightText, ldmText, requirementParts, vehicleTypeText, FORKLIFT_REQUIREMENT } from '../orderCargo';
import { RecordPriceStatistics } from '../PriceStatisticsLine';
import { FEATURE_PRICE_STATISTICS } from '../priceStatistics';
import { useFeature } from '../../featureFlags/FeatureGate';
import { OriginRow } from './OriginTag';
import { clientOrigin, recordOrigin, weightOrigin, type FieldOrigin } from './fieldOrigin';
import { placeText, pointsCountText } from './OrderWindowHeader';
import { ClientContactLog } from '../../csWork/ClientContactLog';
import { FEATURE_CS_LIGHT } from '../../csWork/csWorkApi';

/**
 * Sekcje 1–5 i 7 okna zlecenia (makieta FTL p. Mariusza, pkt 12; Etap 1,
 * decyzja Bartka 15.10.2026). Sekcja 6 („Realizacja”) składa się z kart,
 * które już są (warianty, rekomendacje, akcje…) — składa ją
 * `UserOfferShow.tsx`, tu jest tylko jej rama.
 *
 * Treść to te same pola co w dotychczasowej karcie „Zlecenie”, rozłożone
 * według makiety, z pochodzeniem przy każdym polu. Tam, gdzie aplikacja nie
 * ma danych (instrukcje dla kierowcy, komunikacja z klientem), sekcja mówi
 * „brak danych” i skąd przyjdą — nie zostawiamy pustego miejsca, które
 * wygląda jak błąd.
 */

type Loose = Record<string, unknown>;

const text = (v: unknown): string | null => (typeof v === 'string' && v.trim() !== '' ? v.trim() : null);
const positive = (n: unknown): n is number => typeof n === 'number' && Number.isFinite(n) && n > 0;

/** Które sekcje są rozwinięte — pamiętane w przeglądarce (wygoda, nie stan). */
const OPEN_SECTIONS_KEY = 'orderWindow.openSections';
/** Domyślnie rozwinięta „Trasa i terminy” — jak w makiecie. */
const DEFAULT_OPEN: Record<string, boolean> = { route: true };

function readOpenSections(): Record<string, boolean> {
  try {
    const raw = window.localStorage.getItem(OPEN_SECTIONS_KEY);
    const parsed = raw ? (JSON.parse(raw) as unknown) : null;
    return parsed && typeof parsed === 'object' ? { ...DEFAULT_OPEN, ...(parsed as Record<string, boolean>) } : DEFAULT_OPEN;
  } catch {
    return DEFAULT_OPEN;
  }
}

function writeOpenSection(id: string, open: boolean): void {
  try {
    window.localStorage.setItem(OPEN_SECTIONS_KEY, JSON.stringify({ ...readOpenSections(), [id]: open }));
  } catch {
    // Prywatne okno albo zablokowana pamięć — sekcja i tak się zwija.
  }
}

/** Numer sekcji jak w makiecie. */
const SectionNumber = ({ no }: { no: number }) => (
  <Box
    component="span"
    aria-hidden
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: 22,
      height: 22,
      borderRadius: '50%',
      border: 1,
      borderColor: 'divider',
      fontSize: '0.75rem',
      flexShrink: 0,
    }}
  >
    {no}
  </Box>
);

/**
 * Sekcja zwijana: numer, tytuł i podpowiedź (co jest w środku) widoczna
 * także po zwinięciu — jak `<details>` w makiecie.
 */
export const OrderSection = ({
  id,
  no,
  title,
  hint,
  children,
}: {
  id: string;
  no: number;
  title: string;
  hint?: ReactNode;
  children: ReactNode;
}) => {
  const [open, setOpen] = useState<boolean>(() => readOpenSections()[id] ?? false);
  const toggle = () => {
    setOpen((was) => {
      writeOpenSection(id, !was);
      return !was;
    });
  };
  return (
    <Card variant="outlined" data-testid={`order-section-${id}`}>
      <ButtonBase
        onClick={toggle}
        aria-expanded={open}
        aria-controls={`order-section-${id}-body`}
        sx={{ width: '100%', justifyContent: 'flex-start', textAlign: 'left', px: 2, py: 1.5 }}
      >
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center', width: '100%', minWidth: 0 }}>
          <SectionNumber no={no} />
          <Typography variant="subtitle1" component="h2" sx={{ fontWeight: 600, flexShrink: 0 }}>
            {title}
          </Typography>
          {hint ? (
            // Podpowiedź skracana wielokropkiem — pełna treść w dymku (audyt układu 23.10.2026).
            <Typography
              variant="body2"
              color="text.secondary"
              noWrap
              title={typeof hint === 'string' ? hint : undefined}
              sx={{ minWidth: 0, flexGrow: 1 }}
            >
              {hint}
            </Typography>
          ) : (
            <Box sx={{ flexGrow: 1 }} />
          )}
          {open ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
        </Stack>
      </ButtonBase>
      <Collapse in={open} id={`order-section-${id}-body`}>
        <CardContent sx={{ pt: 0, '&:last-child': { pb: 2 } }}>{children}</CardContent>
      </Collapse>
    </Card>
  );
};

/** Sekcja bez zwijania — „Realizacja”, robocza dla FTL. */
export const OrderWorkSection = ({ no, title, children }: { no: number; title: string; children: ReactNode }) => (
  <Box component="section" data-testid="order-section-execution" aria-label={title}>
    <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 1 }}>
      <SectionNumber no={no} />
      <Typography variant="subtitle1" component="h2" sx={{ fontWeight: 600 }}>
        {title}
      </Typography>
    </Stack>
    <Stack spacing={2}>{children}</Stack>
  </Box>
);

/** „Brak danych” z wyjaśnieniem, skąd przyjdą — zamiast pustej sekcji. */
export const NoDataNote = ({ children, testId }: { children: ReactNode; testId?: string }) => (
  <Stack direction="row" spacing={1} sx={{ alignItems: 'flex-start' }} data-testid={testId}>
    <Chip size="small" variant="outlined" label="brak danych" />
    <Typography variant="body2" color="text.secondary">
      {children}
    </Typography>
  </Stack>
);

const Fields = ({ children }: { children: ReactNode }) => (
  <Table size="small">
    <TableBody>{children}</TableBody>
  </Table>
);

// ── 1. Klient i rozliczenie ─────────────────────────────────────────────

/**
 * Klient, spółka, numery i to, kto wiezie. Zapis do VIA i koperta maila
 * przychodzą z `UserOfferShow.tsx` (tam żyją ich komponenty).
 */
export const ClientSection = ({ viaWrite, preOrderEnvelope }: { viaWrite: ReactNode; preOrderEnvelope?: ReactNode }) => {
  const r = useRecordContext();
  if (!r) return null;
  const origin = recordOrigin(r);
  const contractor = r.contractor as Loose | undefined;
  const company = text((r.company as Loose | undefined)?.name);
  const carrier = text((r.carrier as Loose | undefined)?.name);
  const customerNumbers = Array.isArray(r.tmsCustomerOrderNumbers)
    ? (r.tmsCustomerOrderNumbers as unknown[]).map(text).filter(Boolean)
    : [];
  // Koperta ma numer po poprawce w „Dane dla importera”, wiersz — z prezlecenia.
  const preOrderCustomerNumber =
    text((r.order as Loose | undefined)?.customerOrderNumber) ?? text(r.customerOrderNumber);
  const hint = [text(contractor?.legalName) ?? text((r.preOrder as Loose | undefined)?.clientName), company]
    .filter(Boolean)
    .join(' · ');
  return (
    <OrderSection id="client" no={1} title="Klient i rozliczenie" hint={hint || 'kontrahent nieznany'}>
      <Fields>
        <OriginRow label="Kontrahent" origin={clientOrigin(r)}>
          {text(contractor?.legalName) ?? <ClientFromPreOrder record={r} />}
          {contractor?.blacklisted === true ? (
            <Chip size="small" color="error" variant="outlined" label="zablokowany" sx={{ ml: 1 }} />
          ) : null}
        </OriginRow>
        <OriginRow label="Spółka" origin={company ? origin : null}>
          {company ?? '—'}
        </OriginRow>
        <OriginRow label="Numer" origin={text(r.externalId) ? origin : null}>
          {text(r.externalId) ?? `#${String(r.id)}`}
        </OriginRow>
        {text(r.tmsReferenceNumber) ? (
          <OriginRow label="Nr referencyjny TMS" origin="tms">
            {text(r.tmsReferenceNumber)}
          </OriginRow>
        ) : null}
        {customerNumbers.length > 0 ? (
          <OriginRow label="Numery zamówienia klienta" origin="tms">
            {customerNumbers.join(', ')}
          </OriginRow>
        ) : null}
        {/* Numer zamówienia klienta z prezlecenia (16.10.2026, odpowiedzi
            Marathonu/Euvic, pyt. 27) — po nim import z TMS rozpozna zlecenie. */}
        {preOrderCustomerNumber ? (
          <OriginRow label="Nr zamówienia klienta" origin="preOrder">
            {preOrderCustomerNumber}
          </OriginRow>
        ) : null}
        <Row label="Źródło">
          <SourceCell record={r} />
        </Row>
        {/* Pusto znaczy „realizacja własna”, a nie brak danych. */}
        <OriginRow label="Przewoźnik" origin={carrier ? origin : null}>
          {carrier ?? 'realizacja własna'}
        </OriginRow>
        <OriginRow label="Zapis do VIA" origin={r.order ? 'operator' : null}>
          {viaWrite}
        </OriginRow>
      </Fields>
      {preOrderEnvelope ? <Box sx={{ mt: 2 }}>{preOrderEnvelope}</Box> : null}
    </OrderSection>
  );
};

// ── 2. Trasa i terminy ──────────────────────────────────────────────────

/** Czas jazdy z HERE: „ok. 7,5 h”. */
const hoursText = (seconds: unknown): string | null =>
  positive(seconds) ? `ok. ${plNumber(seconds / 3600, 1)} h jazdy` : null;

/** Punkt na mapie: z geokodowania — wyliczenie; inaczej jak rekord. */
const geoOrigin = (r: Loose): FieldOrigin | null =>
  text(r.fromGeoSource) || text(r.toGeoSource) ? 'calc' : recordOrigin(r);

export const RouteSection = ({ routePoints }: { routePoints: ReactNode }) => {
  const r = useRecordContext();
  if (!r) return null;
  const origin = recordOrigin(r);
  const points = pointsCountText(r);
  const loading = windowText(
    (r.loadingDateFrom ?? r.fromDate) as string | null,
    (r.loadingDateTo as string | null | undefined) ?? null,
  );
  const unloading = windowText(
    (r.unloadingDateFrom as string | null | undefined) ?? null,
    (r.unloadingDateTo ?? r.toDate) as string | null,
  );
  const hint = [points, `załadunek ${loading}`].filter(Boolean).join(' · ');
  return (
    <OrderSection id="route" no={2} title="Trasa i terminy" hint={hint}>
      <Fields>
        <OriginRow label="Załadunek" origin={origin}>
          {placeText(r.fromCountry, r.fromPostalCode, r.fromCity)}
        </OriginRow>
        <OriginRow label="Rozładunek" origin={origin}>
          {placeText(r.toCountry, r.toPostalCode, r.toCity)}
        </OriginRow>
        <OriginRow label="Punkt na mapie" origin={geoOrigin(r)}>
          <GeoPointsInfo record={r} />
        </OriginRow>
        <OriginRow label="Okno załadunku" origin={origin}>
          {loading}
        </OriginRow>
        <OriginRow label="Okno rozładunku" origin={origin}>
          {unloading}
        </OriginRow>
        {r.fixed === true || r.fixed === false ? (
          <OriginRow label="Godzina" origin="tms">
            {r.fixed ? 'stała (fix)' : 'okno (widełki)'}
          </OriginRow>
        ) : null}
        <OriginRow label="Dystans z TMS" origin={positive(r.routeDistanceKm) ? 'tms' : null}>
          {positive(r.routeDistanceKm) ? `${plNumber(r.routeDistanceKm, 0)} km` : '—'}
        </OriginRow>
        {positive(r.hereRouteKm) ? (
          <OriginRow label="Trasa z map HERE" origin="calc">
            {[`${plNumber(r.hereRouteKm, 0)} km`, hoursText(r.hereRouteSeconds)].filter(Boolean).join(' · ')}
          </OriginRow>
        ) : null}
      </Fields>
      <Box sx={{ mt: 2 }}>{routePoints}</Box>
    </OrderSection>
  );
};

// ── 3. Ładunek i pojazd ─────────────────────────────────────────────────

const yesNoText = (v: unknown): string => (v === true ? 'tak' : v === false ? 'nie' : '—');

export const CargoSection = ({ forklift }: { forklift: ReactNode }) => {
  const r = useRecordContext();
  if (!r) return null;
  const origin = recordOrigin(r);
  const { type, forklift: forkliftNote } = vehicleTypeText(r.assignedVehicleType);
  const weight = bankRowWeightText(r.keyPoints, (r.preOrder as RaRecord | undefined)?.weight);
  const hint = [type !== '—' ? type : null, weight !== '—' ? weight : 'waga: brak', ldmText(r.ldm) !== '—' ? `LDM ${ldmText(r.ldm)}` : null]
    .filter(Boolean)
    .join(' · ');
  return (
    <OrderSection id="cargo" no={3} title="Ładunek i pojazd" hint={hint}>
      <Fields>
        {/* Wymaganie zamówienia z TMS (`vehicle_type`), nie typ obsadzonego auta. */}
        <OriginRow label="Wymagany typ pojazdu" origin={type !== '—' ? origin : null}>
          {type}
          {forkliftNote ? (
            <Chip size="small" color="warning" variant="outlined" label={forkliftNote} sx={{ ml: 1 }} />
          ) : null}
        </OriginRow>
        <OriginRow label="LDM" origin={positive(r.ldm) ? origin : null}>
          {ldmText(r.ldm)}
        </OriginRow>
        <OriginRow label="Waga" origin={weightOrigin(r)}>
          {weight}
        </OriginRow>
        <OriginRow label="ADR" origin={r.adr === true || r.adr === false ? origin : null}>
          {yesNoText(r.adr)}
        </OriginRow>
      </Fields>
      {forklift ? <Box sx={{ mt: 2 }}>{forklift}</Box> : null}
    </OrderSection>
  );
};

// ── 4. Cena i finanse ───────────────────────────────────────────────────

const moneyText = (amount: unknown, currency: unknown): string | null => {
  if (amount === null || amount === undefined || amount === '') return null;
  return `${String(amount)} ${text(currency) ?? ''}`.trim();
};

export const PriceSection = ({ vat }: { vat: ReactNode }) => {
  const r = useRecordContext();
  // Statystyka cen z VIA (13.10.2026) — wiersz tylko z przełącznikiem.
  const priceStatistics = useFeature(FEATURE_PRICE_STATISTICS);
  if (!r) return null;
  const price = moneyText(r.price, r.currency);
  const carrierPrice = moneyText(r.carrierPrice, r.carrierPriceCurrency ?? r.currency);
  const vatRate = text((r.vatSuggestion as Loose | undefined)?.rate);
  const hint = [price ?? 'stawka: brak', vatRate ? `VAT: ${vatRate}` : null].filter(Boolean).join(' · ');
  return (
    <OrderSection id="price" no={4} title="Cena i finanse" hint={hint}>
      <Fields>
        <OriginRow label="Stawka" origin={price ? recordOrigin(r) : null}>
          {price ?? '—'}
        </OriginRow>
        {priceStatistics ? (
          <OriginRow label="Cena klienta za km" origin="calc">
            <RecordPriceStatistics record={r} />
          </OriginRow>
        ) : null}
        {carrierPrice ? (
          <OriginRow label="Cena przewoźnika" origin="tms">
            {carrierPrice}
          </OriginRow>
        ) : null}
      </Fields>
      {vat ? <Box sx={{ mt: 2 }}>{vat}</Box> : null}
    </OrderSection>
  );
};

// ── 5. Instrukcje i wymagania ───────────────────────────────────────────

/**
 * TMS nie przekazuje instrukcji dla kierowcy ani wymagań miejsca (brama,
 * rampa, awizacja) — sekcja mówi to wprost. Wymagania, które znamy (ADR,
 * wózek, stała godzina), pokazujemy normalnie (ocena wykonalności dla
 * p. Mariusza, rozdział 3, pkt 8).
 */
export const InstructionsSection = () => {
  const r = useRecordContext();
  if (!r) return null;
  const origin = recordOrigin(r);
  const parts = requirementParts(r.assignedVehicleType);
  const known: Array<{ label: string; value: string }> = [];
  if (r.adr === true) known.push({ label: 'ADR', value: 'wymagany' });
  if (parts.includes(FORKLIFT_REQUIREMENT) && parts.length > 1) known.push({ label: 'Wózek', value: 'wymagany (zestaw z wózkiem)' });
  if (r.fixed === true) known.push({ label: 'Stała godzina', value: 'tak (fix)' });
  const hint = `instrukcje: brak danych${known.length > 0 ? ` · wymagania: ${known.map((k) => k.label).join(', ')}` : ''}`;
  return (
    <OrderSection id="instructions" no={5} title="Instrukcje i wymagania" hint={hint}>
      <NoDataNote testId="instructions-no-data">
        Instrukcji dla kierowcy i wymagań miejsca (brama, rampa, awizacja) TMS nie przekazuje. Przyjdą z modułów
        Customer Service i Administracji w kolejnym etapie projektu.
      </NoDataNote>
      {known.length > 0 ? (
        <Box sx={{ mt: 1 }}>
          <Fields>
            {known.map((k) => (
              <OriginRow key={k.label} label={k.label} origin={origin}>
                {k.value}
              </OriginRow>
            ))}
          </Fields>
        </Box>
      ) : null}
    </OrderSection>
  );
};

// ── 7. Komunikacja z klientem ───────────────────────────────────────────

/**
 * Kontakt po stronie klienta przychodzi z TMS (`customer_contact`) — to
 * mamy. Uwag dla CS i korespondencji z klientem aplikacja nie prowadzi
 * (moduł CS w kolejnym etapie projektu; ręczne zdarzenia czekają na
 * decyzję p. Mariusza nr 7).
 */
export const CommunicationSection = () => {
  const r = useRecordContext();
  // Awizacje i notatki CS (21.10.2026, paczka p. Mariusza 21.10 BR-CS-10;
  // przełącznik `csLight`) — zamiast „brak danych”.
  const csLight = useFeature(FEATURE_CS_LIGHT);
  if (!r) return null;
  const name = text(r.customerContactName);
  const phone = text(r.customerContactPhone);
  const email = text(r.customerContactEmail);
  const hasContact = Boolean(name || phone || email);
  return (
    <OrderSection
      id="communication"
      no={7}
      title="Komunikacja z klientem"
      hint={hasContact ? `kontakt: ${name ?? phone ?? email}` : csLight ? 'awizacje i notatki CS' : 'brak danych'}
    >
      {hasContact ? (
        <Box sx={{ mb: 1 }}>
          <Fields>
            <OriginRow label="Osoba kontaktowa klienta" origin="tms">
              {name ?? '—'}
            </OriginRow>
            <OriginRow label="Telefon" origin={phone ? 'tms' : null}>
              {phone ?? '—'}
            </OriginRow>
            <OriginRow label="E-mail" origin={email ? 'tms' : null}>
              {email ?? '—'}
            </OriginRow>
          </Fields>
        </Box>
      ) : null}
      {csLight ? (
        <ClientContactLog />
      ) : (
        <NoDataNote testId="communication-no-data">
          Uwag dla CS i korespondencji z klientem aplikacja jeszcze nie prowadzi — przyjdą z modułu Customer Service
          w kolejnym etapie projektu.
        </NoDataNote>
      )}
    </OrderSection>
  );
};

/** Zdarzenia z realizacji — dziś z TMS i telematyki, bez wpisów ręcznych. */
export const ExecutionEventsNote = () => (
  <Card variant="outlined">
    <CardContent sx={{ '&:last-child': { pb: 2 } }}>
      <Typography variant="overline" color="text.secondary" sx={{ display: 'block' }}>
        Zdarzenia z realizacji
      </Typography>
      <NoDataNote testId="execution-events-no-data">
        Zdarzenia z realizacji (awaria, nowy szacowany przyjazd, potwierdzenie załadunku i rozładunku) przychodzą dziś
        tylko z TMS i telematyki — status realizacji widać w pasku etapów. Ręcznego wpisywania zdarzeń jeszcze nie ma.
      </NoDataNote>
    </CardContent>
  </Card>
);

